import { isPlatformBrowser } from '@angular/common';
import { InjectionToken, PLATFORM_ID, Provider } from '@angular/core';
import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApmService } from '@elastic/apm-rum-angular';
import { environment } from '@environment';
import { CurrentSession } from '@models/common/current-session';
import { DeviceInfoService } from '@services/device-info.service';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import extractFiles from 'extract-files/public/extractFiles.js';

export const WS_LINK = new InjectionToken<WebSocketLink>('WS_LINK');

const uri = `${environment.coreHttpUrl}/graphql`;
const wsUri = `${environment.coreWsUrl}/graphql`;

// Prefijo `tucuate` de `provideNgxWebstorage` (app.config.ts) + clave `session` de AuthService.
// Se lee directo de sessionStorage porque AuthService depende de Apollo (ciclo de dependencias).
function getSession(): CurrentSession | null {
  // `sessionStorage` no existe en SSR (Node). TODO: la sesión no viaja al servidor todavía, así
  // que en SSR siempre se renderiza sin token ni tenant.
  if (typeof sessionStorage === 'undefined') return null;

  const sessionString = sessionStorage.getItem('tucuate.session');
  if (sessionString == null) return null;

  try {
    return JSON.parse(sessionString) as CurrentSession | null;
  } catch {
    return null;
  }
}

function getToken(): string | null {
  return getSession()?.token ?? null;
}

function getTenantKey(): string | null {
  return getSession()?.tenant?.key ?? null;
}

function createWsLink(): WebSocketLink {
  return new WebSocketLink({
    uri: wsUri,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: async () => {
        const token = getToken();
        return token === null ? {} : { Authorization: `Bearer ${token}` };
      },
    },
  });
}

// Las cuatro operaciones en las que el backend registra el dispositivo. El resto de las cabeceras
// solo viaja acá: `X-Vendor-Id` sobrevive a reinstalaciones y no debe quedar en cada log de acceso.
const DEVICE_REPORTING_OPERATIONS = ['Authenticate', 'AuthenticateByToken', 'ActivateAccount', 'CreateRegisterForm'];

export function createApollo(
  apmService: ApmService,
  httpLink: HttpLink,
  platformId: Object,
  deviceInfoService: DeviceInfoService,
): ApolloClient<any> {
  const http = httpLink.create({
    uri,
    extractFiles,
  });

  const auth = setContext((request, { headers }) => {
    const token = getToken();
    const transaction = apmService.apm.getCurrentTransaction();
    if (transaction) transaction.name = request.operationName;

    const tenantKey = getTenantKey();
    const reportsDevice = DEVICE_REPORTING_OPERATIONS.includes(request.operationName ?? '');

    // `X-Tenant-Key` se omite mientras el usuario no haya elegido tenant.
    return {
      headers: {
        ...headers,
        ...(token !== null && { Authorization: `Bearer ${token}` }),
        ...(tenantKey !== null && { 'X-Tenant-Key': tenantKey }),
        ...(reportsDevice && deviceInfoService.getDeviceHeaders()),
      },
    };
  });

  let apolloLink: ApolloLink;
  if (isPlatformBrowser(platformId)) {
    const ws = createWsLink();
    apolloLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      ws,
      auth.concat(http),
    );
  } else {
    apolloLink = split(({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    }, auth.concat(http));
  }

  return new ApolloClient<any>({
    link: ApolloLink.from([apolloLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      },
      mutate: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      },
    },
  });
}

export const graphqlProviders: Provider[] = [
  Apollo,
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [ApmService, HttpLink, PLATFORM_ID, DeviceInfoService],
  },
];
